
// THis Code that we send Duration selected time and status
import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  HostListener,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { SharedDataService } from '../../shared.data.service';
import { first, Subscription } from 'rxjs';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { UserService } from '../../../../services/core/user.service';
import { DatePipe } from '@angular/common';
import { 
  RAW_STATUSES,
  RESET_TIMES,
  TIME_SLOTS
} from './statistics-dropdown.constant';
import { UserStatusService } from './user-status.service';

interface Status {
  id: string;
  svg: SafeHtml;
  title: string;
  slug: string;
}

interface ResetTime {
  id: string;
  time: string;
}
interface TimeSlots {
  id: number;
  time: string;
}

interface CustomDate {
  date: Date | null;
}

@Component({
    selector: 'app-statistics-dropdown',
    templateUrl: './statistics-dropdown.component.html',
    styleUrls: ['./statistics-dropdown.component.scss'],
    providers: [DatePipe],
    standalone: false
})
export class StatisticsDropdownComponent implements OnInit, OnDestroy {
  status: string = 'Available';
  selectedStatus: Status | null = null;
  statuses: Status[] = [];
  isDurationClicked: boolean = false;
  isDropdownOpen: boolean = false;
  customDate: Date | null = null;
  showDatePicker: boolean = false;
  apiStatuses: any;
  currentStatus: any = [];
  @ViewChild('picker') datePicker!: MatDatepicker<Date>;
  resetTimes: ResetTime[] = RESET_TIMES;
  timeSlots: TimeSlots[] = TIME_SLOTS;
  private rawStatuses = RAW_STATUSES;
  selectedUserStatusFromService: { svg: SafeHtml; title: string } | null = null;     
  private statusSubscription: Subscription | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private elementRef: ElementRef,
    private sharedDataService: SharedDataService,
    public toasterService: ToasterService,
    private currentUser: UserService,
    private datePipe: DatePipe,
    private userStatusService: UserStatusService
  ) {}

  ngOnInit(): void {
    this.fetchUserStatusOptions();
    this.initializeState();
    this.setupFormListeners();
    this.subscribeToStatusStream();
  }

  ngOnDestroy(): void {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
  }

  public userStatusForm: UntypedFormGroup = new UntypedFormGroup({
    status: new UntypedFormControl(null, [Validators.required]),
    resetTime: new UntypedFormControl(null, [Validators.required]),
    timeSlots: new UntypedFormControl(null),
    customDate: new UntypedFormControl(null),
  });

  fetchUserStatusOptions(): void {
    this.sharedDataService
      .getUserStatusOption()
      .pipe(first())
      .subscribe({
        next: (data) => {
          this.apiStatuses = this.getStatusValues(data?.data?.statuses);
        },
      });
  }

 
  getStatusValues(statuses: { label: string; value: string }[]): string[] {
    return statuses.map((status) => status.value);
  }

  initializeState(): void {
    this.currentStatus = this.currentUser.getUserStatus();
    this.initializeStatuses();
    this.selectedStatus =
      this.statuses.find((s) => s.title === this.currentStatus) || null;

    this.userStatusForm.patchValue({
      status: this.currentStatus,
    });

    this.updateSelectedStatusFromCurrentStatus();
  }

  setupFormListeners(): void {
    this.userStatusForm.get('resetTime')?.valueChanges.subscribe(() => {
      this.updateValidators();
    });

    this.userStatusForm.get('status')?.valueChanges.subscribe((newStatus) => {
      this.handleStatusChange(newStatus);
    });
  }

  handleStatusChange(newStatus: any): void {
    if (!newStatus) return;

    let statusObj = newStatus;
    if (typeof newStatus === 'string') {
      statusObj = this.statuses.find((s) => s.title === newStatus) || null;
    } else if (newStatus.title) {
      statusObj =
        this.statuses.find((s) => s.title === newStatus.title) || null;
    }

    if (statusObj) {
      this.selectedStatus = statusObj;
    }
  }

  initializeStatuses(): void {
    this.statuses = this.rawStatuses.map((status) => ({
      ...status,
      svg: this.sanitizer.bypassSecurityTrustHtml(status.svg),
    }));
  }

  /**
   * Keep dropdown status in sync with shared status stream (e.g., backend GET poller).
   */
  private subscribeToStatusStream(): void {
    this.statusSubscription = this.userStatusService.status$.subscribe(
      (statusInfo) => {
        if (!statusInfo || !statusInfo.slug) {
          return;
        }
        this.applyIncomingStatus(statusInfo.slug);
      }
    );
  }

  /**
   * Apply an incoming status slug to local state/form without disrupting duration selections.
   */
  private applyIncomingStatus(statusSlug: string): void {
    // Avoid unnecessary work if already on this status
    if (this.selectedStatus?.slug === statusSlug) {
      return;
    }

    const matching = this.statuses.find((s) => s.slug === statusSlug);
    if (!matching) {
      return; // Unknown slug; ignore
    }

    this.currentStatus = statusSlug;
    this.selectedStatus = matching;
    this.status = matching.title;
    this.selectedUserStatusFromService = this.userStatusService.getCurrentStatus();

    // Patch only the status control to avoid wiping duration inputs
    this.userStatusForm.patchValue(
      { status: matching.title },
      { emitEvent: false }
    );
  }

  updateSelectedStatusFromCurrentStatus(): void {
    const formattedStatus = this.formatStatusString(this.currentStatus);
    const matchingStatus = this.statuses.find(
      (s) => s.title === formattedStatus || s.slug === this.currentStatus
    );
    this.selectedStatus = matchingStatus || this.statuses[0];
  }

  updateValidators(): void {
    const resetTimeValue = this.userStatusForm.get('resetTime')?.value;
    const isCustomTime = resetTimeValue?.time === 'Custom';

    const timeSlotsControl = this.userStatusForm.get('timeSlots');
    const customDateControl = this.userStatusForm.get('customDate');

    if (isCustomTime) {
      timeSlotsControl?.setValidators([Validators.required]);
      customDateControl?.setValidators([Validators.required]);
    } else {
      timeSlotsControl?.clearValidators();
      customDateControl?.clearValidators();
    }

    timeSlotsControl?.updateValueAndValidity();
    customDateControl?.updateValueAndValidity();
  }

  

  getFormData() {
    const formValue = this.userStatusForm?.value;
    const resetTimeValue = formValue.resetTime?.time;
    const currentDate = new Date();
    let duration = null;
    let timeType = null; // New field to store the selected time type
 // Flag to indicate status is on
  
    // Handle duration calculation
    if (resetTimeValue === 'Custom' && formValue.customDate) {
      duration = this.calculateCustomDuration(formValue);
      timeType = 'custom'; // Set time type for custom selection
    } else if (resetTimeValue) {
      // Get the duration and time type for predefined selections
      const durationResult = this.calculatePredefinedDuration(resetTimeValue, currentDate);
      duration = durationResult.duration;
      timeType = durationResult.timeType; // Get the time type from the calculation
    }
  
    // Determine status slug
    const statusSlug = this.determineStatusSlug(formValue.status);
  
    // Return the enhanced payload
    return {
      status: statusSlug,
      duration: duration,
      timeType: timeType, // Include the time type in the payload

    };
  }

  

  calculateCustomDuration(formValue: any): string | null {
    const customDate =
      formValue.customDate instanceof Date
        ? formValue.customDate
        : new Date(formValue.customDate);
  
    // Apply time from selected time slot
    if (formValue.timeSlots?.time) {
      const timeParts = formValue.timeSlots.time.match(/(\d+):(\d+) ([AP]M)/);
      if (timeParts) {
        let hours = parseInt(timeParts[1]);
        const minutes = parseInt(timeParts[2]);
        const isPM = timeParts[3] === 'PM';
  
        // Convert to 24-hour format
        if (isPM && hours < 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;
  
        customDate.setHours(hours, minutes, 0, 0);
      }
    }
  
    return this.datePipe.transform(customDate, 'yyyy-MM-dd HH:mm:ss');
  }

  /**
   * Calculates duration for predefined time selections
   */
  

  calculatePredefinedDuration(
    resetTimeValue: string,
    date: Date
  ): { duration: string | null, timeType: string } {
    const newDate = new Date(date); // Create a copy to avoid modifying the original date
    let timeType = '';
  
    switch (resetTimeValue) {
      case '30 minutes':
        newDate.setMinutes(newDate.getMinutes() + 30);
        timeType = 'minutes_30';
        break;
      case '1 hour':
        newDate.setHours(newDate.getHours() + 1);
        timeType = 'hours_1';
        break;
      case '2 hours':
        newDate.setHours(newDate.getHours() + 2);
        timeType = 'hours_2';
        break;
      case 'Today':
        newDate.setHours(23, 59, 59);
        timeType = 'today';
        break;
      case 'This week':
        const day = newDate.getDay();
        const diff = day === 0 ? 0 : 7 - day;
        newDate.setDate(newDate.getDate() + diff);
        newDate.setHours(23, 59, 59);
        timeType = 'this_week';
        break;
      default:
        timeType = '';
        break;
    }
  
    return {
      duration: this.datePipe.transform(newDate, 'yyyy-MM-dd HH:mm:ss'),
      timeType: timeType
    };
  }

  private determineStatusSlug(statusValue: any): string {
    if (typeof statusValue === 'string') {
      const statusObj = this.statuses.find((s) => s.title === statusValue);
      return statusObj?.slug || 'available';
    }

    if (statusValue?.slug) {
      return statusValue.slug;
    }

    return this.selectedStatus?.slug || 'available';
  }

  onSaveStatus(): void {
    if (this.userStatusForm.invalid) {
      this.touchFormControls();
      return;
    }
    this.updateSelectedStatusFormValue();
    const payload = this.getFormData();
    this.sendStatusUpdate(payload);
  }

  private updateSelectedStatusFormValue(): void {
    const statusValue = this.userStatusForm.get('status')?.value;
    if (!statusValue) return;
    let statusObj = statusValue;
    if (typeof statusValue === 'string') {
      statusObj =
        this.statuses.find((s) => s.title === statusValue) ||
        this.selectedStatus;
    } else if (statusValue?.title) {
      statusObj =
        this.statuses.find((s) => s.title === statusValue.title) ||
        this.selectedStatus;
    }

    this.selectedStatus = statusObj;
  }


  sendStatusUpdate(payload: any): void {
    this.sharedDataService.setUserStatus(payload).subscribe({
      next: (response) => {
        this.handleStatusUpdateSuccess(response);
      },
    });
  }

  handleStatusUpdateSuccess(response: any): void {
    this.currentStatus = response?.data?.data?.user_status;
    this.currentUser.setUserStatus(this.currentStatus);
    
    const statusSlug = this.selectedStatus?.slug || 'available';
    this.userStatusService.setCurrentStatus(statusSlug);
    this.selectedUserStatusFromService = this.userStatusService.getCurrentStatus();

    this.toasterService.showSuccessMessage(response?.data.message);
    this.isDropdownOpen = false;
    this.isDurationClicked = false;
  }

  setStatus(newStatus: string | any, event: Event): void {
    event.stopPropagation();
    this.status = newStatus;
    this.selectedStatus =
      this.statuses.find((s) => s.title === newStatus) || null;

    const statusSlug = this.selectedStatus?.slug || 'available';
    const statusPayload = {
      status: statusSlug,
      duration: null,
    };

    this.sharedDataService.setUserStatus(statusPayload).subscribe({
      next: (response) => {
        this.currentStatus = response?.data?.data?.user_status;
        this.currentUser.setUserStatus(this.currentStatus);
          this.userStatusService.setCurrentStatus(statusSlug);  //service work
          this.selectedUserStatusFromService = this.userStatusService.getCurrentStatus();//service work
          console.log('service Functions',this.selectedUserStatusFromService)
  
        this.toasterService.showSuccessMessage(response?.data.message);
        this.isDropdownOpen = false;
      },
    });
  }

  resetDurationForm(): void {
    this.userStatusForm.patchValue({
      status: this.selectedStatus,
      resetTime: null,
      timeSlots: null,
      customDate: null,
    });
  }

  getStatusBySlug(slug: any): Status | undefined {
    return this.statuses.find((x: any) => x.slug === slug);
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
    this.isDurationClicked = false;
  }

  onDurationClick(event: Event): void {
    event.stopPropagation();
    this.isDurationClicked = true;
    this.resetDurationForm();

    this.userStatusForm.patchValue({
      status: this.selectedStatus,
    });

    const dropdownMenu = this.elementRef.nativeElement.querySelector(
      '.custom-dropdown-menu'
    );
    if (dropdownMenu) {
      dropdownMenu.classList.add('transition-effect');
    }
  }

  menuItemshow(event: Event): void {
    event.stopPropagation();
    this.isDurationClicked = false;

    const dropdownMenu = this.elementRef.nativeElement.querySelector(
      '.custom-dropdown-menu'
    );
    if (dropdownMenu) {
      dropdownMenu.classList.remove('transition-effect');
    }
  }

  touchFormControls(): void {
    Object.keys(this.userStatusForm.controls).forEach((key) => {
      this.userStatusForm.get(key)?.markAsTouched();
    });
  }

  resetStatus(event: Event): void {
    event.stopPropagation();
    this.setStatus('Available', event);
    this.isDropdownOpen = false;
  }

  onDateChange(event: any): void {
    event.stopPropagation();
    this.customDate = event.value;
    this.userStatusForm.patchValue({
      customDate: this.customDate,
    });
  }

  onDatePickerClick(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const isDatePickerClick =
      event.target &&
      (event.target as HTMLElement).closest('.mat-datepicker-content');

    if (
      !this.elementRef.nativeElement.contains(event.target) &&
      !isDatePickerClick
    ) {
      this.isDropdownOpen = false;
    }
  }

  onStatusChange(selectedStatus: Status): void {
    if (selectedStatus) {
      this.selectedStatus = selectedStatus;
    }
  }

  formatStatusString(status: string): string {
    if (!status) return '';

    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
